import React, {useEffect, useState, useCallback, useRef} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Autocomplete as JoyAutocomplete, FormControl, FormLabel, FormHelperText, AutocompleteOption } from '@mui/joy';
import CircularProgress from '@mui/joy/CircularProgress';

const SearchAutocomplete = ({
    label,
    placeholder,
    value,
    onSelect,
    searchAction,
    selector,
    isSearchingSelector,
    getOptionLabel,
    noOptionsText,
    noOptionComponent,
    noOptionAlwaysVisible = false,
    error,
    disabled,
    required,
    sx,
    endDecorator,
    slotProps,
    ...props
}) => {
    const dispatch = useDispatch();
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const debounceTimeoutRef = useRef(null);
    const listboxRef = useRef(null);

    const searchResults = useSelector(selector);
    const isSearching = useSelector(isSearchingSelector || (({ taxonomyName }) => taxonomyName.isSearching));

    const optionsWithCurrentValue = React.useMemo(() => {
        if (!value || !searchResults) {
            return searchResults;
        }
        
        const valueExists = searchResults.some(option => option?.id === value?.id);
        
        if (valueExists) {
            return searchResults;
        }
        
        return [value, ...searchResults];
    }, [value, searchResults]);

    const handleInputChange = useCallback((event, newInputValue) => {
        setInputValue(newInputValue);
        setCurrentPage(0);
        setHasMore(true);
        setIsTyping(true);
        
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        if (!newInputValue || newInputValue.trim().length === 0) {
            setIsTyping(false);
            dispatch(searchAction('', 20, 0));
            return;
        }

        debounceTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
            dispatch(searchAction(newInputValue, 20, 0));
        }, 300);
    }, [dispatch, searchAction]);

    const loadMoreResults = useCallback(() => {
        if (!hasMore || isSearching) return;
        
        const nextPage = currentPage + 1;
        const limit = 20;
        const skip = nextPage * limit;
        
        dispatch(searchAction(inputValue, limit, skip))
            .then((action) => {
                if (action.payload && action.payload.length < limit) {
                    setHasMore(false);
                }
                setCurrentPage(nextPage);
            });
    }, [currentPage, hasMore, isSearching, inputValue, dispatch, searchAction]);

    useEffect(() => {
        dispatch(searchAction('', 20, 0));
        
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const defaultSlotProps = {
        listbox: {
            ref: listboxRef,
            onScroll: (event) => {
                const listboxNode = event.currentTarget;
                if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
                    loadMoreResults();
                }
            },
            sx: {
                maxHeight: 300,
                '& > li:last-child': hasMore && isSearching && currentPage > 0 ? {
                    display: 'flex',
                    justifyContent: 'center',
                    padding: 2
                } : {},
                ...slotProps?.listbox?.sx
            }
        }
    };

    const mergedSlotProps = {
        ...defaultSlotProps,
        ...slotProps,
        listbox: {
            ...defaultSlotProps.listbox,
            ...slotProps?.listbox
        }
    };

    const warning = props.warning;

    return (
        <FormControl required={required} error={!!error} color={!error && warning ? 'warning' : undefined} disabled={disabled}>
            <FormLabel>{label}</FormLabel>
            <JoyAutocomplete
                placeholder={placeholder}
                value={value}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={(event, value) => onSelect(value)}
                options={optionsWithCurrentValue}
                loading={isSearching && currentPage === 0}
                loadingText="Searching..."
                noOptionsText={noOptionsText || (inputValue.trim() ? `No results for "${inputValue}"` : "Start typing to search")}
                getOptionLabel={getOptionLabel}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                endDecorator={
                    endDecorator || (
                        isSearching && currentPage === 0 ? (
                            <CircularProgress size="sm" sx={{ bgcolor: 'transparent' }} />
                        ) : null
                    )
                }
                slotProps={mergedSlotProps}
                sx={sx}
                renderOption={(props, option) => {
                    if (option.isNoOption) {
                        return (
                            <AutocompleteOption {...props}>
                                {noOptionComponent}
                            </AutocompleteOption>
                        );
                    }
                    return <AutocompleteOption {...props}>{getOptionLabel(option)}</AutocompleteOption>;
                }}
                {...props}
            />
            <FormHelperText>{error || warning}</FormHelperText>
        </FormControl>
    );
};

export default SearchAutocomplete;